import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowUpRight } = FiIcons;

const services = [
  {
    title: 'Sports Injury Rehab',
    description: 'Specialized care for sprains, strains, and returning athletes to peak performance safely.',
    // Gym/Workout context - Reliable
    image: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&q=80&w=800',
    link: '#contact'
  },
  {
    title: 'Post-Surgical Recovery',
    description: 'Comprehensive rehabilitation programs for ACL, hip, knee, and shoulder surgeries.',
    // Medical/Recovery context
    image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80&w=800',
    link: '#contact'
  },
  {
    title: 'Chronic Pain Management',
    description: 'Long-term pain relief solutions for back, neck, and joint conditions using manual therapy.',
    // Massage/Hands-on context - Fixed Image
    image: 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?auto=format&fit=crop&q=80&w=800',
    link: '#contact'
  },
  {
    title: 'Postural Correction',
    description: 'Ergonomic assessments and strengthening to fix desk-related strain and poor posture.',
    // Spine/Back context
    image: 'https://images.unsplash.com/photo-1516062423079-7ca13cdc7f5a?auto=format&fit=crop&q=80&w=800',
    link: '#contact'
  },
  {
    title: 'Vestibular Therapy',
    description: 'Targeted exercises to manage dizziness, vertigo, and improve overall balance.',
    // Balance/Focus context
    image: 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?auto=format&fit=crop&q=80&w=800',
    link: '#contact'
  }
];

const ServiceCard = ({ service }) => {
  return (
    <div className="w-[300px] md:w-[380px] flex-shrink-0 mx-4 relative group">
      <div className="bg-white rounded-3xl overflow-hidden h-full flex flex-col shadow-sm hover:shadow-2xl hover:shadow-emerald-900/10 transition-all duration-500 border border-slate-100">
        
        {/* Image Container */}
        <div className="relative h-64 overflow-hidden">
          <div className="absolute inset-0 bg-slate-900/10 group-hover:bg-transparent transition-colors duration-500 z-10"></div>
          <img 
            src={service.image} 
            alt={service.title} 
            className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
          />
          
          {/* Floating Action Button - Reveals on Hover */}
          <div className="absolute bottom-4 right-4 z-20">
            <div className="w-12 h-12 bg-white/90 backdrop-blur-md rounded-full flex items-center justify-center text-slate-900 shadow-lg transform translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
               <SafeIcon icon={FiArrowUpRight} className="w-5 h-5" />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-8 flex flex-col flex-grow">
          <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-emerald-600 transition-colors duration-300">
            {service.title}
          </h3>
          <p className="text-slate-500 text-sm leading-relaxed line-clamp-3 font-medium">
            {service.description}
          </p>
        </div>
      </div>
    </div>
  );
};

const Services = () => {
  return (
    <section id="services" className="py-20 lg:py-28 bg-slate-50 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 mb-12 md:mb-16 text-center">
        {/* Header Badge */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="inline-block bg-white px-4 py-1.5 rounded-full shadow-sm mb-4 md:mb-6 border border-slate-200"
        >
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
            <span className="text-slate-900 font-bold text-xs uppercase tracking-wider">Our Expertise</span>
          </div>
        </motion.div>

        {/* Typography */}
        <motion.h2 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-4 tracking-tight"
        >
          Clinical Excellence
        </motion.h2>
        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="text-lg text-slate-600 max-w-2xl mx-auto"
        >
          Advanced physiotherapy treatments tailored to your unique recovery needs.
        </motion.p>
      </div>

      {/* Infinite Carousel Slider */}
      <motion.div 
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 1 }}
        className="relative w-full"
      >
        <div className="flex animate-marquee hover:pause whitespace-nowrap py-8">
          {services.map((service, index) => (
            <ServiceCard key={`s1-${index}`} service={service} />
          ))}
          {/* Duplicate for seamless loop */}
          {services.map((service, index) => (
            <ServiceCard key={`s2-${index}`} service={service} />
          ))}
        </div>
      </motion.div>
    </section>
  );
};

export default Services;